import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { authService, clearTokens, getAccessToken, onLogout, saleService } from "../services";
import { DEFAULT_SALES_CONFIG, normalizeSalesConfig } from "../config/permissionUtils";
import {
  hasActiveSalesInStore,
  releaseAllUserReservations,
  releaseAllUserReservationsSync,
} from "../Components/sale/saleReservationRelease";
import { clearAllSalesDrafts } from "../state/salesDraftsSlice";
import { clearAllTabs } from "../tabs/tabsSlice";

const AuthContext = createContext(null);

function findCurrentUserBranch(userData) {
  const userBranches = userData?.user_branches || [];
  if (!Array.isArray(userBranches) || userBranches.length === 0) {
    return null;
  }

  return userBranches.find((item) => item?.current) || userBranches[0] || null;
}

function getBranchPerms(userBranch) {
  const perms = userBranch?.effective_permission_codes || [];
  return Array.isArray(perms) ? perms : [];
}

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [user, setUser] = useState(null);
  const [currentBranch, setCurrentBranchState] = useState(null);
  const [effectivePerms, setEffectivePerms] = useState([]);
  const [salesConfig, setSalesConfig] = useState(DEFAULT_SALES_CONFIG);
  const [salesConfigLoading, setSalesConfigLoading] = useState(false);

  const applyMePayload = useCallback((mePayload) => {
    setUser(mePayload || null);

    const currentUserBranch = findCurrentUserBranch(mePayload);
    setCurrentBranchState(currentUserBranch?.branch || null);
    setEffectivePerms(getBranchPerms(currentUserBranch));
  }, []);

  const loadMe = useCallback(async () => {
    setLoadingAuth(true);

    try {
      const meFn = authService?.getMe || authService?.me;
      if (typeof meFn !== "function") {
        return null;
      }

      const mePayload = await meFn();
      applyMePayload(mePayload);
      return mePayload;
    } catch (error) {
      if (!getAccessToken()) {
        setUser(null);
        setCurrentBranchState(null);
        setEffectivePerms([]);
        setSalesConfig(DEFAULT_SALES_CONFIG);
      }
      return null;
    } finally {
      setLoadingAuth(false);
    }
  }, [applyMePayload]);

  const login = useCallback(
    async (credentials) => {
      const loginFn = authService?.login;
      if (typeof loginFn !== "function") {
        throw new Error("authService.login is not available.");
      }

      const loginResponse = await loginFn(credentials);
      await loadMe();
      saleService.cleanupExpiredReservations().catch(() => null);
      return loginResponse;
    },
    [loadMe]
  );

  const logout = useCallback(async (options = {}) => {
    if (hasActiveSalesInStore() && !options?.confirmedRelease) {
      return { requiresConfirmation: true };
    }
    try {
      await releaseAllUserReservations(currentBranch?.id);
      if (typeof authService?.logout === "function") {
        await authService.logout();
      } else {
        clearTokens();
      }
    } catch {
      // Ignore remote logout failures; local logout state is handled below.
    } finally {
      dispatch(clearAllTabs());
      dispatch(clearAllSalesDrafts());
      setUser(null);
      setCurrentBranchState(null);
      setEffectivePerms([]);
      setSalesConfig(DEFAULT_SALES_CONFIG);
      setLoadingAuth(false);
    }
    return { ok: true };
  }, [currentBranch?.id, dispatch]);

  const setCurrentBranch = useCallback(
    (branchOrId) => {
      const userBranches = user?.user_branches || [];
      if (!Array.isArray(userBranches) || userBranches.length === 0) {
        setCurrentBranchState(null);
        setEffectivePerms([]);
        setSalesConfig(DEFAULT_SALES_CONFIG);
        return;
      }

      const branchId =
        typeof branchOrId === "object" ? branchOrId?.id : branchOrId;

      const selectedUserBranch =
        userBranches.find((item) => item?.branch?.id === branchId) ||
        userBranches.find((item) => item?.current) ||
        userBranches[0];

      setCurrentBranchState(selectedUserBranch?.branch || null);
      setEffectivePerms(getBranchPerms(selectedUserBranch));
    },
    [user]
  );

  useEffect(() => {
    let mounted = true;

    if (!getAccessToken()) {
      setLoadingAuth(false);
    } else {
      loadMe().then(() => {
        saleService.cleanupExpiredReservations().catch(() => null);
      });
    }

    const unsubscribe = onLogout(() => {
      if (!mounted) {
        return;
      }
      clearTokens();
      dispatch(clearAllTabs());
      dispatch(clearAllSalesDrafts());
      setUser(null);
      setCurrentBranchState(null);
      setEffectivePerms([]);
      setSalesConfig(DEFAULT_SALES_CONFIG);
      setLoadingAuth(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [dispatch, loadMe]);

  const isAuthenticated = Boolean(getAccessToken() || user);
  const isSuperuser = Boolean(user?.is_superuser);
  const permissions = effectivePerms;

  const refreshSalesConfig = useCallback(async () => {
    const branchId = currentBranch?.id || null;
    if (!branchId || !isAuthenticated) {
      setSalesConfig(DEFAULT_SALES_CONFIG);
      return DEFAULT_SALES_CONFIG;
    }

    setSalesConfigLoading(true);
    try {
      const data = await saleService.getSalesConfig({ branch_id: branchId });
      const normalized = normalizeSalesConfig(data, branchId);
      setSalesConfig(normalized);
      return normalized;
    } catch {
      const fallback = normalizeSalesConfig(DEFAULT_SALES_CONFIG, branchId);
      setSalesConfig(fallback);
      return fallback;
    } finally {
      setSalesConfigLoading(false);
    }
  }, [currentBranch?.id, isAuthenticated]);

  useEffect(() => {
    refreshSalesConfig();
  }, [refreshSalesConfig]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const releaseSync = () => releaseAllUserReservationsSync(currentBranch?.id);
    window.addEventListener("pagehide", releaseSync);
    window.addEventListener("beforeunload", releaseSync);
    return () => {
      window.removeEventListener("pagehide", releaseSync);
      window.removeEventListener("beforeunload", releaseSync);
    };
  }, [currentBranch?.id, isAuthenticated]);

  const contextValue = useMemo(
    () => ({
      loadingAuth,
      isAuthenticated,
      isSuperuser,
      user,
      currentBranch,
      effectivePerms,
      permissions,
      salesConfig,
      salesConfigLoading,
      login,
      logout,
      loadMe,
      refreshSalesConfig,
      setCurrentBranch,
    }),
    [
      loadingAuth,
      isAuthenticated,
      isSuperuser,
      user,
      currentBranch,
      effectivePerms,
      permissions,
      salesConfig,
      salesConfigLoading,
      login,
      logout,
      loadMe,
      refreshSalesConfig,
      setCurrentBranch,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used within AuthProvider.");
  }
  return value;
}

export default AuthContext;
